/**
 * Copyright (c) 2025 Stefan Radig
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { base64ToBytes, bytesToBase64 } from './base64';

/**
 * Encryption utilities.
 *
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │ DO NOT "FIX" THE DERIVATION IN PLACE. IT WOULD DESTROY ALL DATA.     │
 * └─────────────────────────────────────────────────────────────────────┘
 *
 * Two things below look wrong to a reviewer or a scanner, and are flagged
 * by every audit (C-2 and C-3 in SECURITY_AUDIT.md):
 *
 *   1. SALT is a fixed application-wide constant, not per-document.
 *   2. deriveFileId() runs AES-GCM with a deterministic IV (see the comment
 *      at that function).
 *
 * Both are ACCEPTED RISKS, deliberately left as-is. Neither is exploitable:
 * security rests on the 128-bit CSPRNG entropy of the document ID (see
 * SECURITY.md), and inverting the derivation is a 2^128 preimage search
 * regardless of the salt. The iterations below do not add meaningful
 * strength for a 128-bit random input - they are only insurance in case
 * that entropy assumption ever regresses.
 *
 * WHY IT CANNOT BE FIXED HERE: the fileId locates a document's SQLite
 * database on the server (data/metadata/<shard>/<fileId>.db), which holds
 * both its content and its write tokens. Changing ANY input to these
 * functions - the salt, the iteration count, the hash, the GCM step -
 * changes the fileId of EVERY EXISTING DOCUMENT and points every existing
 * link at a database that does not exist. There is no migration path:
 * document IDs live only in users' URL fragments and are never sent to the
 * server, so nobody - not even the operator - can enumerate the documents
 * to re-key them.
 *
 * The clean construction (per-document salt, PBKDF2 -> HKDF, no GCM) is
 * fully specified in docs/security/v2-key-derivation-spec.md as a versioned
 * scheme: new documents opt into "v2." derivation, existing ones keep this
 * code forever. Implement that if the calculus ever changes - e.g. if
 * document IDs ever come from an external source or a weaker RNG. Until
 * then, treat everything below as a frozen compatibility contract.
 */
export class EncryptionManager {
    // Application-specific salt - unique to Editoor collaborative editor.
    // Fixed across all documents (audit C-2): accepted risk, frozen. See the
    // class comment - changing this orphans every existing document.
    private static readonly SALT = 'editoor.collaborative.editor.2025';
    private static readonly ITERATIONS = 100000;

    // Cache for derived file IDs (documentId -> fileId)
    // This avoids repeated expensive PBKDF2 derivations
    private static fileIdCache = new Map<string, string>();

    // Cache for derived encryption keys (documentId -> CryptoKey)
    private static encryptionKeyCache = new Map<string, CryptoKey>();

    /**
     * Derive the file ID from the document ID (the document ID itself is never
     * sent to the server).
     *
     * FROZEN - see the class comment. Every byte of this function is a
     * compatibility contract with every document that already exists.
     *
     * The AES-GCM step below (with a deterministic IV derived from the
     * document ID) is audit finding C-3. It is an ACCEPTED RISK and NOT
     * exploitable, for three independent reasons:
     *
     *   - The AES key is itself derived from the document ID, so two documents
     *     sharing an IV prefix have DIFFERENT keys. IV reuse across different
     *     keys is cryptographically meaningless.
     *   - The only same-key case is the same document encrypting the same
     *     plaintext, which yields the same ciphertext. That is determinism -
     *     the entire point of this function - not nonce reuse.
     *   - The GCM output NEVER LEAVES this function. It is immediately hashed
     *     with SHA-256 and truncated, so there is no ciphertext for an attacker
     *     to see, XOR, or forge against.
     *
     * The GCM step is nonetheless pointless here (a hash alone would do) and is
     * a misuse pattern that must NOT be copied anywhere else in the codebase -
     * anywhere the ciphertext is exposed, or one key encrypts multiple
     * messages, a deterministic IV IS a real vulnerability.
     */
    static async deriveFileId(documentId: string): Promise<string> {
        // Check cache first
        const cached = this.fileIdCache.get(documentId);
        if (cached) {
            return cached;
        }

        // Create input string: application salt + documentId
        const input = this.SALT + documentId;
        
        // Convert to ArrayBuffer for crypto operations
        const inputBuffer = new TextEncoder().encode(input);
        
        // Derive key using PBKDF2
        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            inputBuffer,
            { name: 'PBKDF2' },
            false,
            ['deriveBits']
        );
        
        // Use fixed application salt for PBKDF2 (not the documentId)
        const saltBuffer = new TextEncoder().encode(this.SALT);
        
        // Derive 32 bytes (256 bits) for AES-256
        const derivedKey = await crypto.subtle.deriveBits(
            {
                name: 'PBKDF2',
                salt: saltBuffer,
                iterations: this.ITERATIONS,
                hash: 'SHA-256'
            },
            keyMaterial,
            256
        );
        
        // Encrypt the input string with the derived key
        const cryptoKey = await crypto.subtle.importKey(
            'raw',
            derivedKey,
            { name: 'AES-GCM' },
            false,
            ['encrypt']
        );
        
        // Use a fixed IV for deterministic file IDs
        const iv = new Uint8Array(12);
        const ivSource = new TextEncoder().encode(documentId.slice(0, 12));
        iv.set(ivSource);
        
        const encryptedData = await crypto.subtle.encrypt(
            {
                name: 'AES-GCM',
                iv: iv
            },
            cryptoKey,
            inputBuffer
        );
        
        // Hash the encrypted data with SHA-256
        const hashBuffer = await crypto.subtle.digest('SHA-256', encryptedData);
        
        // Convert to hex string for file ID (first 32 characters only)
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

        const fileId = hashHex.substring(0, 32);

        // Store in cache for future calls
        this.fileIdCache.set(documentId, fileId);

        return fileId;
    }

    // Derive encryption key from document ID
    static async deriveEncryptionKey(documentId: string): Promise<CryptoKey> {
        // Check cache first
        const cached = this.encryptionKeyCache.get(documentId);
        if (cached) {
            return cached;
        }

        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            new TextEncoder().encode(documentId),
            { name: 'PBKDF2' },
            false,
            ['deriveKey']
        );

        const saltBuffer = new TextEncoder().encode(this.SALT);

        const key = await crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: saltBuffer,
                iterations: this.ITERATIONS,
                hash: 'SHA-256'
            },
            keyMaterial,
            {
                name: 'AES-GCM',
                length: 256
            },
            false,
            ['encrypt', 'decrypt']
        );

        // Store in cache
        this.encryptionKeyCache.set(documentId, key);

        return key;
    }
    
    // Encrypt data
    static async encrypt(data: string, key: CryptoKey): Promise<string> {
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const encodedData = new TextEncoder().encode(data);
        
        const encryptedData = await crypto.subtle.encrypt(
            {
                name: 'AES-GCM',
                iv: iv
            },
            key,
            encodedData
        );
        
        // Combine IV and encrypted data
        const combined = new Uint8Array(iv.length + encryptedData.byteLength);
        combined.set(iv);
        combined.set(new Uint8Array(encryptedData), iv.length);
        
        return bytesToBase64(combined);
    }
    
    // Decrypt data
    static async decrypt(encryptedData: string, key: CryptoKey): Promise<string> {
        try {
            const combined = base64ToBytes(encryptedData);

            // Extract IV and encrypted data (views, no copies)
            const iv = combined.subarray(0, 12);
            const data = combined.subarray(12);

            const decryptedData = await crypto.subtle.decrypt(
                {
                    name: 'AES-GCM',
                    iv: iv
                },
                key,
                data
            );

            return new TextDecoder().decode(decryptedData);
        } catch (error) {
            console.error('Decryption failed:', error);
            throw new Error('Failed to decrypt data');
        }
    }

    // Encrypt binary data directly (no base64 encoding)
    // Returns: IV (12 bytes) + ciphertext + auth tag (16 bytes)
    static async encryptBinary(data: Uint8Array, key: CryptoKey): Promise<Uint8Array> {
        const iv = crypto.getRandomValues(new Uint8Array(12));
        // Properly handle subarrays by slicing to get just the data we need
        const dataBuffer = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
        const encryptedBuffer = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            dataBuffer
        );
        // Combine IV + ciphertext (auth tag is appended by AES-GCM)
        const result = new Uint8Array(12 + encryptedBuffer.byteLength);
        result.set(iv, 0);
        result.set(new Uint8Array(encryptedBuffer), 12);
        return result;
    }

    // Decrypt binary data directly (no base64 decoding)
    // Expects: IV (12 bytes) + ciphertext + auth tag (16 bytes)
    static async decryptBinary(encryptedData: Uint8Array, key: CryptoKey): Promise<Uint8Array> {
        // Views, not copies: this runs for every cached record at open. (The
        // stored and decoded buffers are never SharedArrayBuffers.)
        const iv = encryptedData.subarray(0, 12) as Uint8Array<ArrayBuffer>;
        const ciphertext = encryptedData.subarray(12) as Uint8Array<ArrayBuffer>;
        const decryptedBuffer = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            ciphertext
        );
        return new Uint8Array(decryptedBuffer);
    }
}